import {CurrencyConfiguration} from "@next/utils"
import {
    Breadcrumbs,
    ItemsPerRow,
    SaleSashConfig,
    AdsConfig,
    ContainerMaxWidth,
    ColourChipsPerRow,
} from "models/SearchState"
import {ItemsPerPage} from "models/ItemsPerPage"
import {Imagery, SavingsSashConfig} from "../ProductSummary"

export const FeatureSettingKeys = {
    breadcrumbsConfig: "plp.breadcrumbs.config",
    enableBrandDisplay: "plp.product-brand-display.is-enabled",
    enableFavourites: "plp.favourites.is-enabled",
    enableFurtherReductionsSash: "plp.further-reductions-sash.is-enabled",
    enableStructuredBrowsing: "plp.structuredbrowsing.is-enabled",
    enableTaxonomy: "plp.taxonomy.is-enabled",
    enableQuickAddToBag: "plp.quick-add-to-bag.is-enabled",
    forceDesktopView: "plp.force-desktop-view.is-enabled",
    globalPriceFormattingConfig: "global.price-formatting.config",
    productSummaryTemplate: "plp.product-template.name",
    imagesConfig: "plp.images.config",
    itemsPerRow: "plp.items-per-row.config",
    itemsPerPage: "plp.items-per-page.config",
    itemsCacheSize: "plp.items-cache-size.config",
    productSingleLineTitle: "plp.product-single-line-title.is-enabled",
    redirectHeader: "plp.redirect-header.is-enabled",
    redirectOldUrlsToNoResults: "plp.redirect-to-no-results.is-enabled",
    saleSashConfig: "plp.sale-sash.config",
    savingsSashConfig: "plp.savings-sash.config",
    template: "plp.template.name",
    useProductAggregator: "plp.use-product-aggregator.is-enabled",
    adsConfig: "plp.ads.config",
    adsVersion: "plp.ads.version",
    cacheControlDuration: "plp.cache-control.duration",
    imageCdnDomainUrl: "global.image-cdn.domain-url",
    themeName: "global.theme.name",
    coremediaDomainUrl: "coremedia.domain.url",
    coremediaRelativeUrl: "coremedia.relative.url",
    coremediaAuthenticationTokenName: "coremedia.authentication.tokenname",
    coremediaAuthenticationTokenValue: "coremedia.authentication.tokenvalue",
    containerMaxWidth: "plp.container-max-width.config",
    enableUnification: "plp.unification.is-enabled",
    colourChipsPerRow: "plp.colour-chips-per-row.config",
    enableReviewStars: "plp.product-review-stars.is-enabled",
    displayFitIcons: "plp.display-fit-icons.is-enabled",
    enableSearchBannerCarousel: "plp.search-banner-carousel.is-enabled",
} as const

export type FeatureSettingKey = (typeof FeatureSettingKeys)[keyof typeof FeatureSettingKeys]

export type ValueObject<T> = {
    Value: T
}

export interface FeatureSettings {
    [FeatureSettingKeys.breadcrumbsConfig]: Breadcrumbs
    [FeatureSettingKeys.enableBrandDisplay]: boolean
    [FeatureSettingKeys.enableFavourites]: boolean
    [FeatureSettingKeys.enableFurtherReductionsSash]: boolean
    [FeatureSettingKeys.enableStructuredBrowsing]: boolean
    [FeatureSettingKeys.enableTaxonomy]: boolean
    [FeatureSettingKeys.enableQuickAddToBag]: boolean
    [FeatureSettingKeys.forceDesktopView]: boolean
    [FeatureSettingKeys.globalPriceFormattingConfig]: CurrencyConfiguration
    [FeatureSettingKeys.imagesConfig]: Imagery
    [FeatureSettingKeys.itemsPerRow]: ItemsPerRow
    [FeatureSettingKeys.productSingleLineTitle]: boolean
    [FeatureSettingKeys.redirectOldUrlsToNoResults]: boolean
    [FeatureSettingKeys.redirectHeader]: boolean
    [FeatureSettingKeys.saleSashConfig]: SaleSashConfig
    [FeatureSettingKeys.savingsSashConfig]: SavingsSashConfig
    [FeatureSettingKeys.template]: string
    [FeatureSettingKeys.productSummaryTemplate]: string
    [FeatureSettingKeys.useProductAggregator]: boolean
    [FeatureSettingKeys.enableStructuredBrowsing]: boolean
    [FeatureSettingKeys.imagesConfig]: Imagery
    [FeatureSettingKeys.productSingleLineTitle]: boolean
    [FeatureSettingKeys.enableFurtherReductionsSash]: boolean
    [FeatureSettingKeys.savingsSashConfig]: SavingsSashConfig
    [FeatureSettingKeys.enableQuickAddToBag]: boolean
    [FeatureSettingKeys.itemsPerRow]: ItemsPerRow
    [FeatureSettingKeys.itemsPerPage]: ItemsPerPage
    [FeatureSettingKeys.itemsCacheSize]: number
    [FeatureSettingKeys.enableBrandDisplay]: boolean
    [FeatureSettingKeys.saleSashConfig]: SaleSashConfig
    [FeatureSettingKeys.adsConfig]: AdsConfig
    [FeatureSettingKeys.adsVersion]: string
    [FeatureSettingKeys.cacheControlDuration]: number
    [FeatureSettingKeys.imageCdnDomainUrl]: string
    [FeatureSettingKeys.themeName]: string
    [FeatureSettingKeys.coremediaDomainUrl]: string
    [FeatureSettingKeys.coremediaRelativeUrl]: string
    [FeatureSettingKeys.coremediaAuthenticationTokenName]: string
    [FeatureSettingKeys.coremediaAuthenticationTokenValue]: string
    [FeatureSettingKeys.containerMaxWidth]: ContainerMaxWidth
    [FeatureSettingKeys.enableUnification]: boolean
    [FeatureSettingKeys.colourChipsPerRow]: ColourChipsPerRow
    [FeatureSettingKeys.enableReviewStars]: boolean
    [FeatureSettingKeys.displayFitIcons]: boolean
    [FeatureSettingKeys.enableSearchBannerCarousel]: boolean
}

/**
 * Defines our Empire level settings for the feature flags
 */
export const defaultFeatFlagValues: FeatureSettings = {
    [FeatureSettingKeys.breadcrumbsConfig]: {
        enabled: false,
        breadcrumbsIcons: {
            enableBackButton: false,
            enableHomeIcon: false,
        },
    },
    [FeatureSettingKeys.enableBrandDisplay]: false,
    [FeatureSettingKeys.enableFavourites]: true,
    [FeatureSettingKeys.enableFurtherReductionsSash]: false,
    [FeatureSettingKeys.enableStructuredBrowsing]: false,
    [FeatureSettingKeys.enableTaxonomy]: false,
    [FeatureSettingKeys.enableQuickAddToBag]: false,
    [FeatureSettingKeys.forceDesktopView]: false,
    [FeatureSettingKeys.productSummaryTemplate]: "standard",
    [FeatureSettingKeys.globalPriceFormattingConfig]: {
        currencySymbol: "",
        spaceRequired: false,
        position: "right",
        decimalSeparatorSymbol: ".",
        groupSeparatorSymbol: "",
        showNumOfDecimalDigits: 2,
        textDirection: "ltr",
    },
    [FeatureSettingKeys.imagesConfig]: {
        folder: "SearchAlt",
        akamaiImageManagerParams: {lg: "", sm: ""},
        dimension: "678x904",
        ratio: "3x4",
        usePDPImage: false,
    },
    [FeatureSettingKeys.itemsPerRow]: {
        xs: 2,
        sm: 2,
        md: 3,
        lg: 4,
        xl: 4,
        xxl: 5,
    },
    [FeatureSettingKeys.itemsPerPage]: {
        initial: {
            desktop: 12,
            mobile: 12,
            tablet: 12,
        },
        subsequent: {
            desktop: 12,
            mobile: 12,
            tablet: 12,
        },
    },
    [FeatureSettingKeys.itemsCacheSize]: 84,
    [FeatureSettingKeys.productSingleLineTitle]: true,
    [FeatureSettingKeys.redirectHeader]: false,
    [FeatureSettingKeys.redirectOldUrlsToNoResults]: false,
    [FeatureSettingKeys.saleSashConfig]: {
        type: "Sale",
        position: "TL",
    },
    [FeatureSettingKeys.adsConfig]: {
        enabled: false,
        ads: [
            {
                tileCount: 2,
                positions: {
                    xl: 4,
                    xs: 2,
                    lg: 4,
                    md: 4,
                    sm: 3,
                    xxl: 4,
                },
                id: 1,
            },
        ],
    },
    [FeatureSettingKeys.adsVersion]: "007",
    [FeatureSettingKeys.savingsSashConfig]: {
        sale: 0,
        clearance: 0,
    },
    [FeatureSettingKeys.template]: "standard",
    [FeatureSettingKeys.useProductAggregator]: false,
    [FeatureSettingKeys.cacheControlDuration]: 10800,
    [FeatureSettingKeys.imageCdnDomainUrl]: "https://cdn.platform.next",
    [FeatureSettingKeys.themeName]: "",
    [FeatureSettingKeys.coremediaDomainUrl]: "https://preview.uat.next.coremedia.cloud",
    [FeatureSettingKeys.coremediaRelativeUrl]: "https://preview.uat.next.coremedia.cloud",
    [FeatureSettingKeys.coremediaAuthenticationTokenName]: "CoreMedia-PreviewAccessToken",
    [FeatureSettingKeys.coremediaAuthenticationTokenValue]: "",
    [FeatureSettingKeys.containerMaxWidth]: {
        fixedMaxWidth: "xl",
        customMaxWidth: null,
    },
    [FeatureSettingKeys.enableUnification]: false,
    [FeatureSettingKeys.colourChipsPerRow]: {
        lg: 7,
        xl: 8,
    },
    [FeatureSettingKeys.enableReviewStars]: false,
    [FeatureSettingKeys.displayFitIcons]: true,
    [FeatureSettingKeys.enableSearchBannerCarousel]: false,
}
